import axios from "axios";

// Mirrors the endpoint resolution used in Redux/AdminReducer/action.js
const getEndpoint = () =>
  window.location.protocol === "http:"
    ? "http://localhost:5001"
    : `https://${window.location.host}/api`;

const authHeaders = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("clinic-application-jwt"));
    return userData?.token ? { Authorization: `Bearer ${userData.token}` } : {};
  } catch (e) {
    return {};
  }
};

const unwrap = (res) => res?.data?.data;

/* ============================ vital templates ============================ */

export const fetchTemplates = async (params = {}) => {
  const res = await axios.get(`${getEndpoint()}/vitals/templates`, { headers: authHeaders(), params });
  return unwrap(res) || [];
};

export const fetchTemplate = async (templateId) => {
  const res = await axios.get(`${getEndpoint()}/vitals/templates/${templateId}`, { headers: authHeaders() });
  return unwrap(res);
};

export const createTemplate = async (payload) => {
  const res = await axios.post(`${getEndpoint()}/vitals/templates`, payload, { headers: authHeaders() });
  return unwrap(res);
};

export const updateTemplate = async (templateId, payload) => {
  const res = await axios.put(`${getEndpoint()}/vitals/templates/${templateId}`, payload, { headers: authHeaders() });
  return unwrap(res);
};

export const deleteTemplate = async (templateId) => {
  const res = await axios.delete(`${getEndpoint()}/vitals/templates/${templateId}`, { headers: authHeaders() });
  return res?.data;
};

/* ============================== assignments ============================== */

export const fetchAssignments = async (patientId, params = {}) => {
  const res = await axios.get(`${getEndpoint()}/vitals/patient/${patientId}/assignments`, {
    headers: authHeaders(),
    params,
  });
  return unwrap(res) || [];
};

export const assignTemplate = async (patientId, payload) => {
  const res = await axios.post(`${getEndpoint()}/vitals/patient/${patientId}/assignments`, payload, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

export const updateAssignment = async (assignmentId, payload) => {
  const res = await axios.put(`${getEndpoint()}/vitals/assignments/${assignmentId}`, payload, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

export const unassignTemplate = async (assignmentId) => {
  const res = await axios.delete(`${getEndpoint()}/vitals/assignments/${assignmentId}`, { headers: authHeaders() });
  return res?.data;
};

/* ================================ readings =============================== */

export const fetchRecords = async (patientId, params = {}) => {
  const res = await axios.get(`${getEndpoint()}/vitals/patient/${patientId}/records`, {
    headers: authHeaders(),
    params,
  });
  return res?.data;
};

export const createRecord = async (patientId, payload) => {
  const res = await axios.post(`${getEndpoint()}/vitals/patient/${patientId}/records`, payload, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

export const updateRecord = async (recordId, payload) => {
  const res = await axios.put(`${getEndpoint()}/vitals/records/${recordId}`, payload, { headers: authHeaders() });
  return unwrap(res);
};

export const deleteRecord = async (recordId) => {
  const res = await axios.delete(`${getEndpoint()}/vitals/records/${recordId}`, { headers: authHeaders() });
  return res?.data;
};

/** Doctor-only trend data behind the auto graphs. */
export const fetchSeries = async (patientId, params = {}) => {
  const res = await axios.get(`${getEndpoint()}/vitals/patient/${patientId}/series`, {
    headers: authHeaders(),
    params,
  });
  return unwrap(res);
};

/* =============================== documents =============================== */

export const fetchDocuments = async (patientId, params = {}) => {
  const res = await axios.get(`${getEndpoint()}/patient-documents/patient/${patientId}`, {
    headers: authHeaders(),
    params,
  });
  return unwrap(res) || [];
};

export const uploadDocuments = async (patientId, files, meta = {}) => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append('files', file));
  Object.entries(meta).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') form.append(key, value);
  });

  const res = await axios.post(`${getEndpoint()}/patient-documents/patient/${patientId}`, form, {
    headers: authHeaders(),
  });
  return unwrap(res) || [];
};

export const updateDocument = async (documentId, payload) => {
  const res = await axios.put(`${getEndpoint()}/patient-documents/${documentId}`, payload, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

export const deleteDocument = async (documentId) => {
  const res = await axios.delete(`${getEndpoint()}/patient-documents/${documentId}`, { headers: authHeaders() });
  return res?.data;
};

/**
 * File routes are behind the JWT, which an <img src> or <a href> cannot send.
 * Fetch as a blob and hand back an object URL instead; callers must revoke it.
 */
export const fetchDocumentObjectUrl = async (documentId) => {
  const res = await axios.get(`${getEndpoint()}/patient-documents/${documentId}/file`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  return URL.createObjectURL(res.data);
};

export const downloadDocument = async (documentId, filename) => {
  const url = await fetchDocumentObjectUrl(documentId);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'file';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/* ============================ profile overview =========================== */

export const fetchPatientOverview = async (patientId) => {
  const res = await axios.get(`${getEndpoint()}/patient-portal/patient/${patientId}/overview`, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

/* ============================= portal access ============================= */

export const fetchAccessLinks = async (patientId) => {
  const res = await axios.get(`${getEndpoint()}/patient-portal/patient/${patientId}/access`, {
    headers: authHeaders(),
  });
  return unwrap(res) || [];
};

export const createAccessLink = async (patientId, payload = {}) => {
  const res = await axios.post(
    `${getEndpoint()}/patient-portal/patient/${patientId}/access`,
    { origin: window.location.origin, ...payload },
    { headers: authHeaders() }
  );
  return unwrap(res);
};

export const updateAccessLink = async (accessId, payload = {}) => {
  const res = await axios.put(`${getEndpoint()}/patient-portal/access/${accessId}`, payload, {
    headers: authHeaders(),
  });
  return unwrap(res);
};

export const revokeAccessLink = async (accessId) => {
  const res = await axios.post(`${getEndpoint()}/patient-portal/access/${accessId}/revoke`, {}, {
    headers: authHeaders(),
  });
  return res?.data;
};

export const sendAccessLink = async (accessId, payload = {}) => {
  const res = await axios.post(
    `${getEndpoint()}/patient-portal/access/${accessId}/send`,
    { origin: window.location.origin, ...payload },
    { headers: authHeaders() }
  );
  return res?.data;
};

/** The link handed to the patient. */
export const buildPortalLink = (token) => `${window.location.origin}/public/portal/${token}`;

/* ========================= patient side (no login) ======================= */

export const fetchPortalView = async (token) => {
  const res = await axios.get(`${getEndpoint()}/patient-portal/public/${token}`);
  return unwrap(res);
};

export const savePortalProfile = async (token, payload) => {
  const res = await axios.put(`${getEndpoint()}/patient-portal/public/${token}/profile`, payload);
  return res?.data;
};

export const submitPortalVitals = async (token, payload) => {
  const res = await axios.post(`${getEndpoint()}/patient-portal/public/${token}/vitals`, payload);
  return unwrap(res);
};

export const updatePortalVitals = async (token, recordId, payload) => {
  const res = await axios.put(`${getEndpoint()}/patient-portal/public/${token}/vitals/${recordId}`, payload);
  return unwrap(res);
};

export const uploadPortalFiles = async (token, files, meta = {}) => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append('files', file));
  Object.entries(meta).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') form.append(key, value);
  });

  const res = await axios.post(`${getEndpoint()}/patient-portal/public/${token}/uploads`, form);
  return unwrap(res) || [];
};

export const deletePortalFile = async (token, documentId) => {
  const res = await axios.delete(`${getEndpoint()}/patient-portal/public/${token}/file/${documentId}`);
  return res?.data;
};

/** Diet questionnaire through the portal link; the server files it against the link's patient. */
export const submitPortalDietForm = async (token, answers) => {
  const res = await axios.post(`${getEndpoint()}/patient-portal/public/${token}/diet`, answers);
  return res?.data;
};

// The token in the path is the credential, so these URLs work directly in
// <img src> and <a href> without an Authorization header.
export const portalFileUrl = (token, documentId, download = false) =>
  `${getEndpoint()}/patient-portal/public/${token}/file/${documentId}${download ? '?download=true' : ''}`;

/* ============================ diet questionnaire ============================ */

// Forms the patient filled in from a link (currently the diet / nutrition one).
export const fetchPatientTherapyForms = async (patientId, params = {}) => {
  const res = await axios.get(`${getEndpoint()}/alternative_therapy/patient/${patientId}`, {
    headers: authHeaders(),
    params,
  });
  return unwrap(res) || [];
};
